import { useEffect, useRef, type ReactElement } from 'react';

import { playerController } from '@/lib/music/player-controller';
import { useMusicPlayerStore } from '@/lib/music-player-store';

/**
 * Hidden global media element — the actual playback engine. Mounted once
 * in App.tsx, it owns the HTMLMediaElement instance, syncs `src` to
 * `store.currentTrack` (or the hardsub mp4 when in karaoke mode), mirrors
 * play/pause/seek/volume commands from the controller, and forwards every
 * event back into the store so every UI surface (sticky bar, drawer,
 * immersive view, lyrics) stays in lockstep with reality.
 *
 * `<video>` is used (not `<audio>`) so karaoke mode can flip the source
 * to the hardsub mp4 and play its audio track natively. The element stays
 * `display: none` — the immersive view renders its OWN visible `<video>`
 * that time-syncs to this engine, so karaoke mode can still render burnt-
 * in lyrics even when the user toggles between music and karaoke without
 * the immersive panel open.
 *
 * Nothing else in the app mounts a media element — all playback flows
 * through this single instance via the controller bridge.
 */
export function GlobalMusicEngine(): ReactElement {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const currentTrack = useMusicPlayerStore((s) => s.currentTrack);
  const playbackMode = useMusicPlayerStore((s) => s.playbackMode);
  const trackId = currentTrack?.id ?? null;
  // In karaoke mode the engine plays the hardsubbed mp4 so the user
  // hears the karaoke audio mix (when KM ships a separate one) AND so
  // the immersive overlay can render the same source visually without
  // a second network round-trip — the proxy caches the bytes hard.
  const isKaraokeSource =
    playbackMode === 'karaoke' && Boolean(currentTrack?.hardsubUrl);
  const trackLink = !currentTrack
    ? null
    : isKaraokeSource
      ? currentTrack.hardsubUrl
      : currentTrack.link;
  const isPlaying = useMusicPlayerStore((s) => s.isPlaying);
  const volume = useMusicPlayerStore((s) => s.volume);
  const isMuted = useMusicPlayerStore((s) => s.isMuted);
  const loop = useMusicPlayerStore((s) => s.loop);

  const setPlaying = useMusicPlayerStore((s) => s.setPlaying);
  const setBuffering = useMusicPlayerStore((s) => s.setBuffering);
  const setCurrentTime = useMusicPlayerStore((s) => s.setCurrentTime);
  const setDuration = useMusicPlayerStore((s) => s.setDuration);
  const playNext = useMusicPlayerStore((s) => s.playNext);

  // Register the media element with the controller for the lifetime of
  // this component. Unregister on unmount so a torn-down node doesn't
  // get commands.
  useEffect(() => {
    playerController.registerEngine(videoRef.current);
    return () => playerController.registerEngine(null);
  }, []);

  // Track-change effect — full reset, since switching tracks invalidates
  // any saved playback position. Optimistically resumes play if the user
  // intent was Playing.
  useEffect(() => {
    const media = videoRef.current;
    if (!media) return;
    if (!trackLink) {
      media.removeAttribute('src');
      media.load();
      return;
    }
    media.src = trackLink;
    media.load();
    if (isPlaying) {
      media.play().catch(() => setPlaying(false));
    }
    setCurrentTime(0);
    setDuration(0);
    // We intentionally don't list isPlaying or trackLink/playback-mode
    // as deps here — the src-swap effect below handles the mode change
    // case, and isPlaying changes shouldn't restart the track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId]);

  // Mode-change effect — swap between mp3 and hardsub mp4 within the
  // same track. Preserves playback position so the toggle feels
  // instantaneous (modulo upstream buffering).
  useEffect(() => {
    const media = videoRef.current;
    if (!media || !trackLink) return;
    if (media.src === trackLink || media.currentSrc === trackLink) return;
    const resumeTime = Number.isFinite(media.currentTime)
      ? media.currentTime
      : 0;
    const wasPlaying = !media.paused;
    media.src = trackLink;
    media.load();
    const onLoadedMetadata = (): void => {
      if (resumeTime > 0 && Number.isFinite(resumeTime)) {
        media.currentTime = resumeTime;
      }
      if (wasPlaying || isPlaying) {
        media.play().catch(() => setPlaying(false));
      }
    };
    media.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
    return () => {
      media.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
    // Only react to src changes that come from a playback-mode swap on
    // the same track — track changes are handled by the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackLink, trackId]);

  // Pure play/pause sync — doesn't touch `src`.
  useEffect(() => {
    const media = videoRef.current;
    if (!media) return;
    if (isPlaying && media.paused) {
      media.play().catch(() => setPlaying(false));
    } else if (!isPlaying && !media.paused) {
      media.pause();
    }
  }, [isPlaying, setPlaying]);

  // Volume + mute pass-through.
  useEffect(() => {
    const media = videoRef.current;
    if (!media) return;
    media.volume = volume;
    media.muted = isMuted;
  }, [volume, isMuted]);

  // Wire media events back into the store.
  useEffect(() => {
    const media = videoRef.current;
    if (!media) return;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTimeUpdate = () => setCurrentTime(media.currentTime || 0);
    const onDurationChange = () =>
      setDuration(Number.isFinite(media.duration) ? media.duration : 0);
    const onLoadedMetadata = onDurationChange;
    const onEnded = () => {
      if (loop === 'one') {
        media.currentTime = 0;
        media.play().catch(() => setPlaying(false));
      } else {
        playNext();
      }
    };
    const onWaiting = () => setBuffering(true);
    const onStalled = () => setBuffering(true);
    const onCanPlay = () => setBuffering(false);
    const onPlaying = () => setBuffering(false);
    const onError = () => {
      setBuffering(false);
      setPlaying(false);
    };

    media.addEventListener('play', onPlay);
    media.addEventListener('pause', onPause);
    media.addEventListener('timeupdate', onTimeUpdate);
    media.addEventListener('durationchange', onDurationChange);
    media.addEventListener('loadedmetadata', onLoadedMetadata);
    media.addEventListener('ended', onEnded);
    media.addEventListener('waiting', onWaiting);
    media.addEventListener('stalled', onStalled);
    media.addEventListener('canplay', onCanPlay);
    media.addEventListener('playing', onPlaying);
    media.addEventListener('error', onError);

    return () => {
      media.removeEventListener('play', onPlay);
      media.removeEventListener('pause', onPause);
      media.removeEventListener('timeupdate', onTimeUpdate);
      media.removeEventListener('durationchange', onDurationChange);
      media.removeEventListener('loadedmetadata', onLoadedMetadata);
      media.removeEventListener('ended', onEnded);
      media.removeEventListener('waiting', onWaiting);
      media.removeEventListener('stalled', onStalled);
      media.removeEventListener('canplay', onCanPlay);
      media.removeEventListener('playing', onPlaying);
      media.removeEventListener('error', onError);
    };
  }, [
    loop,
    playNext,
    setBuffering,
    setCurrentTime,
    setDuration,
    setPlaying,
  ]);

  return (
    <video
      ref={videoRef}
      preload="metadata"
      playsInline
      // Hidden from layout — the engine has no visual UI. All controls
      // are rendered by the music-player-bar / drawer / immersive views.
      // The immersive view renders its own visible `<video>` element
      // (muted, same src) that time-syncs to this engine for the
      // burnt-in karaoke visual.
      style={{ display: 'none' }}
      aria-hidden="true"
    />
  );
}

export default GlobalMusicEngine;
